"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { z } from "zod/v3";
import { trpc } from "@/lib/trpc-client";

const LEGACY_CREATOR_TOKEN_KEY = "nf-one-portal-token";

const hintKey = "newform-creator-link-hint";
const hintSchema = z.object({
  creatorId: z.string(),
  email: z.string().email(),
  orgSlug: z.string(),
});
type LinkHint = z.infer<typeof hintSchema>;

async function optionalHint(request: Promise<LinkHint | null>) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    // Guidance must not prevent sign-in when an old-link lookup stalls. Ignore
    // a late result; the session and current membership determine access.
    return await Promise.race([
      request.catch(() => null),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), 10_000);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

export function clearLegacyCreatorHint() {
  try {
    sessionStorage.removeItem(hintKey);
  } catch {
    /* Storage is optional. */
  }
}

/** Old tokens are used once for navigation guidance, never as new credentials. */
export function useLegacyCreatorLink() {
  const params = useSearchParams();
  const paramToken = params.get("token");
  const pendingToken = useRef<string | null>(null);
  const lookup = useRef<{
    token: string;
    result: Promise<LinkHint | null>;
  } | null>(null);
  const utils = trpc.useUtils();
  const [state, setState] = useState<{
    ready: boolean;
    wasLegacy: boolean;
    hint: LinkHint | null;
  }>({ ready: false, wasLegacy: false, hint: null });
  useEffect(() => {
    let cancelled = false;
    async function readLink() {
      let token = paramToken ?? pendingToken.current;
      let savedHint: LinkHint | null = null;
      try {
        token ??= sessionStorage.getItem(LEGACY_CREATOR_TOKEN_KEY);
        sessionStorage.removeItem(LEGACY_CREATOR_TOKEN_KEY);
        sessionStorage.removeItem("nfone-inbox-session");
        const stored = z
          .object({ hint: hintSchema, expires: z.number() })
          .safeParse(JSON.parse(sessionStorage.getItem(hintKey) ?? "null"));
        if (stored.success && stored.data.expires > Date.now())
          savedHint = stored.data.hint;
        // A newly opened link supersedes earlier guidance, including when the
        // new link is expired. Otherwise URL cleanup can resurrect a stale hint.
        if (token) {
          sessionStorage.removeItem(hintKey);
          savedHint = null;
        }
      } catch {
        /* Storage is optional. */
      }
      pendingToken.current = token;
      // Scrub before any network wait, including stalled lookups. Keep the
      // token only in this hook while the optional navigation hint resolves.
      const incomingUrl = new URL(window.location.href);
      if (incomingUrl.searchParams.has("token")) {
        incomingUrl.searchParams.delete("token");
        window.history.replaceState(
          window.history.state,
          "",
          `${incomingUrl.pathname}${incomingUrl.search}${incomingUrl.hash}`,
        );
      }
      let hint = savedHint;
      if (token) {
        try {
          if (lookup.current?.token !== token)
            lookup.current = {
              token,
              result: optionalHint(
                utils.client.creatorAuth.legacyLink.mutate({ token }),
              ),
            };
          hint = await lookup.current.result;
        } catch {
          hint = null;
        }
      }
      if (cancelled) return;
      pendingToken.current = null;
      // Preserve the destination and explicit program while adding guidance.
      const url = new URL(window.location.href);
      if (hint) url.searchParams.delete("chooseProgram");
      if (!url.searchParams.has("orgSlug")) {
        if (hint) url.searchParams.set("orgSlug", hint.orgSlug);
        // Survive a Google redirect without guessing which program an expired
        // or unavailable hint referred to. This marker grants no access.
        else if (token) url.searchParams.set("chooseProgram", "true");
      }
      if (token || hint) {
        window.history.replaceState(
          window.history.state,
          "",
          `${url.pathname}${url.search}${url.hash}`,
        );
      }
      if (hint) {
        try {
          sessionStorage.setItem(
            hintKey,
            JSON.stringify({ hint, expires: Date.now() + 10 * 60_000 }),
          );
        } catch {
          /* Optional guidance only. */
        }
      }
      setState((previous) => ({
        ready: true,
        wasLegacy:
          previous.wasLegacy ||
          !!token ||
          !!hint ||
          url.searchParams.get("chooseProgram") === "true",
        hint,
      }));
    }
    void readLink();
    return () => {
      cancelled = true;
    };
  }, [paramToken, utils]);
  return state;
}
